

import { Component, ChangeDetectionStrategy, inject, signal, OnInit, computed } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReportingService } from '../../services/reporting.service';
import { AuthService } from '../../services/auth.service';
import { AuditService } from '../../services/audit.service';
import { DataService } from '../../services/data.service';
import { ReportPht27Item, ReportPht28Item, Product, ClinicSettings } from '../../models/index';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';

declare var html2canvas: any;

const ROWS_PER_PAGE_PHT27 = 20;
const ROWS_PER_PAGE_PHT28 = 15;

// Interface for the rich form row object
interface Pht27FormRow extends ReportPht27Item {
  id?: string; // Add ID for saved entries
  suggestions?: Product[];
  isDropdownOpen?: boolean;
  isNew?: boolean; // Flag to track new rows
}

@Component({
  selector: 'app-compliance-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, RouterLink, DecimalPipe, TranslatePipe, ConfirmationModalComponent],
  templateUrl: './compliance-reports.component.html',
  styleUrls: ['./compliance-reports.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplianceReportsComponent implements OnInit {
  private reportingService = inject(ReportingService);
  private authService = inject(AuthService);
  private auditService = inject(AuditService); 
  private dataService = inject(DataService);
  private document: Document = inject(DOCUMENT);
  private notificationService = inject(NotificationService);

  activeTab = signal<'pht27' | 'pht28'>('pht28');
  hasAccess = this.authService.hasFeature('compliance_reports');
  
  selectedMonth = signal<string>(new Date().toISOString().slice(0, 7)); // YYYY-MM

  // Report Configuration (Header Settings)
  reportLicenseName = signal('');
  reportLicenseNumber = signal('');
  registrarType = signal<'CENTRAL' | 'PROVINCIAL'>('PROVINCIAL');
  provinceName = signal('');
  isSavingSettings = signal(false); // New state for saving config

  // Data States
  pht27Data = signal<Pht27FormRow[]>([]);
  pht28Data = signal<ReportPht28Item[]>([]);
  allProducts = signal<Product[]>([]); // For autocomplete
  isLoading = signal(false);
  isSaving = signal(false);
  isGenerating = signal(false);
  editingRowId = signal<string | null>(null);

  // Confirmation Modal State
  showSaveConfirm = signal(false);
  showDeleteConfirm = signal<Pht27FormRow | null>(null);
  private dataForSave = signal<Pht27FormRow[]>([]);
  
  saveConfirmationMessage = computed(() => {
    const count = this.dataForSave().length;
    return `คุณกำลังจะบันทึก ${count} รายการใหม่ ระบบจะปรับสต็อกและสร้างสินค้าใหม่หากไม่พบชื่อในคลัง ยืนยันหรือไม่?`;
  });
  
  // Pagination State
  currentPage = signal(1);

  rowsPerPage = computed(() => this.activeTab() === 'pht27' ? ROWS_PER_PAGE_PHT27 : ROWS_PER_PAGE_PHT28);

  totalPages = computed(() => {
      const totalRows = this.activeTab() === 'pht27' ? this.pht27Data().length : this.pht28Data().length;
      if (totalRows === 0) return 1;
      return Math.ceil(totalRows / this.rowsPerPage());
  });

  paginatedPht27Data = computed(() => {
    const data = this.pht27Data();
    const page = this.currentPage();
    const rpp = this.rowsPerPage();
    const startIndex = (page - 1) * rpp;
    return data.slice(startIndex, startIndex + rpp);
  });

  paginatedPht28Data = computed(() => {
    const data = this.pht28Data();
    const page = this.currentPage();
    const rpp = this.rowsPerPage();
    const startIndex = (page - 1) * rpp;
    return data.slice(startIndex, startIndex + rpp);
  });
  
  indexOffset = computed(() => (this.currentPage() - 1) * this.rowsPerPage());

  ngOnInit(): void {
    if (this.hasAccess()) {
        this.loadData();
        this.preloadSettings();
        this.dataService.getProducts().then(prods => this.allProducts.set(prods)); // Load products for autocomplete
    }
  }

  // Modified: Load saved settings from clinic_settings
  async preloadSettings() {
      try {
          const settings = await this.dataService.getClinicSettings();
          const user = this.authService.currentUser();
          const store = this.authService.currentStore();
          
          if (settings) {
            // Priority: reportLicenseNumber -> licenseNumber (fallback) -> empty
            if (settings.reportLicenseNumber) {
                this.reportLicenseNumber.set(settings.reportLicenseNumber);
            } else if (settings.licenseNumber) {
                this.reportLicenseNumber.set(settings.licenseNumber);
            }

            if (settings.reportLicenseName) {
                this.reportLicenseName.set(settings.reportLicenseName);
            } else if (store?.name) {
                this.reportLicenseName.set(store.name); 
            } else if (user?.name) {
                this.reportLicenseName.set(user.name);
            }

            if (settings.reportRegistrarType) this.registrarType.set(settings.reportRegistrarType);
            if (settings.reportProvinceName) this.provinceName.set(settings.reportProvinceName);
          }
      } catch (e) {
          console.warn("Could not preload settings", e);
      }
  }

  // New: Save Header Settings to DB
  async saveHeaderSettings() {
      this.isSavingSettings.set(true);
      try {
          const currentSettings = await this.dataService.getClinicSettings() || {} as ClinicSettings;
          
          const newSettings: ClinicSettings = {
              ...currentSettings,
              // FIX: Do NOT update licenseNumber here. Use specialized field.
              reportLicenseNumber: this.reportLicenseNumber(), 
              reportLicenseName: this.reportLicenseName(),
              reportRegistrarType: this.registrarType(),
              reportProvinceName: this.provinceName()
          };

          await this.dataService.updateClinicSettings(newSettings);
          this.notificationService.showSuccess('บันทึกการตั้งค่าหัวกระดาษเรียบร้อยแล้ว');
      } catch (e) {
          console.error(e);
          this.notificationService.showError('บันทึกการตั้งค่าไม่สำเร็จ');
      } finally {
          this.isSavingSettings.set(false);
      }
  }

  setTab(tab: 'pht27' | 'pht28'): void {
    this.activeTab.set(tab);
    this.currentPage.set(1);
    this.loadData();
  }
  
  private createEmptyPht27Row(): Pht27FormRow {
    return { 
      dateReceived: new Date().toISOString().slice(0,10), 
      licenseNumber: '', supplierName: '', description: '', quantity: 0, 
      unit: 'GRAM', cultivationArea: undefined,
      suggestions: [], isDropdownOpen: false, isNew: true // Mark as new row
    };
  }

  async loadData(): Promise<void> {
    this.isLoading.set(true);
    this.currentPage.set(1);
    try {
      if (this.activeTab() === 'pht27') {
        const data = await this.reportingService.getPht27Report(this.selectedMonth());
        const mappedData = data.map(d => ({ ...d, id: (d as any).id, suggestions: [], isDropdownOpen: false, isNew: false }));
        this.pht27Data.set(mappedData.length > 0 ? mappedData : [this.createEmptyPht27Row()]);
        this.pht28Data.set([]);
      } else {
        const data = await this.reportingService.getPht28Report(this.selectedMonth());
        this.pht28Data.set(data);
        this.pht27Data.set([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.isLoading.set(false);
    }
  }
  
  addPht27Row(): void {
    this.pht27Data.update(current => [...current, this.createEmptyPht27Row()]);
    this.currentPage.set(this.totalPages());
  }

  deleteNewPht27Row(indexOnPage: number): void {
    const absoluteIndex = this.indexOffset() + indexOnPage;
    this.pht27Data.update(current => {
        if (absoluteIndex >= 0 && absoluteIndex < current.length) {
            const newArr = [...current];
            newArr.splice(absoluteIndex, 1);
            return newArr.length > 0 ? newArr : [this.createEmptyPht27Row()];
        }
        return current;
    });
    
    if (this.currentPage() > this.totalPages()) {
        this.currentPage.set(this.totalPages());
    }
  }

  updateSuggestions(row: Pht27FormRow) {
    const searchTerm = (row.description || '').toLowerCase();
    if (searchTerm.length > 1) {
      row.suggestions = this.allProducts().filter(p => 
        p.name.toLowerCase().includes(searchTerm) || 
        (p.sku && p.sku.toLowerCase().includes(searchTerm))
      ).slice(0, 5);
      row.isDropdownOpen = (row.suggestions?.length || 0) > 0;
    } else {
      row.suggestions = [];
      row.isDropdownOpen = false;
    }
  }

  onDescriptionFocus(row: Pht27FormRow) { this.updateSuggestions(row); }
  selectSuggestion(row: Pht27FormRow, product: Product) { row.description = product.name; row.isDropdownOpen = false; }
  onDescriptionBlur(row: Pht27FormRow) { setTimeout(() => { row.isDropdownOpen = false; }, 200); }
  
  // --- SAVE, EDIT, DELETE FLOW ---
  
  requestSaveNewEntries(): void {
    const dataToSave = this.pht27Data().filter(row => row.isNew && row.description && row.quantity > 0);
    if (dataToSave.length === 0) {
      this.notificationService.showError('No new data to save. Please add a new row with a description and quantity.');
      return;
    }
    this.dataForSave.set(dataToSave);
    this.showSaveConfirm.set(true);
  }

  cancelSaveNewEntries(): void { 
    this.showSaveConfirm.set(false);
    this.dataForSave.set([]);
  }

  async confirmSaveNewEntries(): Promise<void> {
    this.showSaveConfirm.set(false);
    const dataToSave = this.dataForSave();
    
    if (dataToSave.length === 0) {
        this.notificationService.showInfo('No new entries to save.');
        return;
    }
    
    this.isSaving.set(true);
    try {
        await this.reportingService.batchSavePht27(dataToSave);
        this.notificationService.showSuccess('บันทึกข้อมูลใหม่และปรับสต็อกเรียบร้อยแล้ว');
        await this.dataService.getProducts().then(prods => this.allProducts.set(prods));
        await this.loadData();
    } catch (e) {
        console.error(e);
        this.notificationService.showError('เกิดข้อผิดพลาดในการบันทึก: ' + (e as Error).message);
    } finally {
        this.isSaving.set(false);
        this.dataForSave.set([]);
    }
  }

  startEditing(row: Pht27FormRow): void { this.editingRowId.set(row.id!); }
  cancelEditing(): void { this.editingRowId.set(null); this.loadData(); }

  async saveEdit(row: Pht27FormRow): Promise<void> {
    if (!row.id) return;
    this.isSaving.set(true);
    try {
      await this.reportingService.updatePht27Entry(row.id, row);
      this.notificationService.showSuccess('แก้ไขข้อมูลเรียบร้อยแล้ว');
      this.editingRowId.set(null);
      await this.loadData();
    } catch (e) {
      console.error(e);
      this.notificationService.showError('เกิดข้อผิดพลาดในการแก้ไข: ' + (e as Error).message);
    } finally {
      this.isSaving.set(false);
    }
  }

  requestDeleteSavedEntry(row: Pht27FormRow): void {
    if (!row.id) return;
    this.showDeleteConfirm.set(row);
  }
  
  cancelDeleteSavedEntry(): void { this.showDeleteConfirm.set(null); }

  async confirmDeleteSavedEntry(): Promise<void> {
    const rowToDelete = this.showDeleteConfirm();
    if (!rowToDelete?.id) return;
    
    this.isSaving.set(true);
    this.showDeleteConfirm.set(null);
    try {
      await this.reportingService.deletePht27Entry(rowToDelete.id);
      this.notificationService.showSuccess('ลบข้อมูลและปรับสต็อกคืนเรียบร้อยแล้ว');
      await this.loadData();
    } catch(e) {
      console.error(e);
      this.notificationService.showError('เกิดข้อผิดพลาดในการลบ: ' + (e as Error).message);
    } finally {
        this.isSaving.set(false);
    }
  }

  // --- PAGINATION CONTROLS ---
  nextPage(): void { this.currentPage.update(p => (p < this.totalPages() ? p + 1 : p)); }
  previousPage(): void { this.currentPage.update(p => (p > 1 ? p - 1 : p)); }

  // --- PRINT & EXPORT ---
  printReport(): void { window.print(); }

  exportToExcel(): void {
    const isPht27 = this.activeTab() === 'pht27';
    
    // THE FIX: Log the export event with the correct type
    if (isPht27) {
        this.auditService.logDataExport('PHT27_REPORT');
    } else {
        this.auditService.logDataExport('PHT28_REPORT (SENSITIVE)');
    }
    
    const filename = isPht27 ? `Pht27_${this.selectedMonth()}.csv` : `Pht28_${this.selectedMonth()}.csv`;
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
    if (isPht27) {
        csvContent += "ลำดับ,วัน/เดือน/ปี ที่ได้มา,เลขที่ใบอนุญาตของผู้จำหน่าย,ชื่อผู้รับอนุญาตที่จำหน่ายให้,รายการ (Description),ขนาดพื้นที่เพาะปลูก(ตร.ม.),จำนวนช่อดอกที่เก็บไว้(กรัม)\n";
        this.pht27Data().forEach((row, index) => {
            csvContent += `${index + 1},${row.dateReceived},"${row.licenseNumber}","${row.supplierName}","${row.description}",${row.cultivationArea || '-' },${row.quantity}\n`;
        });
    } else {
        csvContent += "ลำดับ,วันเดือนปี,เลขบัตรประชาชน,ชื่อผู้ซื้อ,วันเดือนปีเกิด,วัตถุประสงค์,เลขที่ใบอนุญาต(ถ้ามี),ปริมาณ(กรัม)\n";
        this.pht28Data().forEach((row, index) => {
            const date = new Date(row.transactionDate).toLocaleDateString('th-TH');
            const dob = row.buyerBirthDate ? new Date(row.buyerBirthDate).toLocaleDateString('th-TH') : '';
            csvContent += `${index + 1},"${date}","${row.buyerId}","${row.buyerName}","${dob}","ใช้เอง",-,${row.quantity}\n`;
        });
    }
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async downloadAsPng(): Promise<void> {
    if (typeof html2canvas === 'undefined') {
      this.notificationService.showInfo('Library not ready. Please try again in a few seconds.');
      return;
    }
    const sourceElement = this.document.getElementById('report-content');
    if (!sourceElement) return;
    this.isGenerating.set(true);
    try {
      const container = this.document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '-9999px';
      container.style.left = '-9999px';
      container.style.width = '794px'; 
      this.document.body.appendChild(container);
      const clone = sourceElement.cloneNode(true) as HTMLElement;
      clone.style.transform = 'none';
      clone.style.margin = '0';
      clone.style.boxShadow = 'none';
      clone.style.width = '100%';
      clone.style.height = 'auto'; 
      clone.style.color = '#000000';
      container.appendChild(clone);
      const canvas = await html2canvas(clone, { scale: 2.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 794 });
      this.document.body.removeChild(container);
      const link = this.document.createElement('a');
      const reportName = this.activeTab().toUpperCase();
      link.download = `Report_${reportName}_${this.selectedMonth()}_Page${this.currentPage()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error("PNG Gen Error:", err);
      this.notificationService.showError("Failed to generate image.");
    } finally {
      this.isGenerating.set(false);
    }
  }

  get thaiYear(): number { return parseInt(this.selectedMonth().split('-')[0]) + 543; }
  get thaiMonth(): string {
      const months = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
      const monthIndex = parseInt(this.selectedMonth().split('-')[1]) - 1;
      return months[monthIndex] || "";
  }
}